'use client'

import { cn } from '@/lib/utils'

interface LoadingOverlayProps {
  isLoading: boolean
  children: React.ReactNode
  className?: string
  message?: string
  size?: 'sm' | 'md' | 'lg'
}

export function LoadingOverlay({
  isLoading,
  children,
  className,
  message = 'Loading...',
  size = 'md',
}: LoadingOverlayProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  }

  const spinnerSizeClasses = {
    sm: 'border-2',
    md: 'border-3',
    lg: 'border-4',
  }

  if (!isLoading) {
    return <>{children}</>
  }

  return (
    <div className={cn('relative inline-block', className)}>
      {/* Child content that will be overlaid */}
      <div
        className={cn(
          'transition-opacity duration-200',
          isLoading ? 'opacity-30 pointer-events-none' : 'opacity-100'
        )}
      >
        {children}
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-lg">
          <div className="flex flex-col items-center space-y-2">
            <div
              className={cn(
                'animate-spin rounded-full border-gray-400 border-t-transparent',
                sizeClasses[size],
                spinnerSizeClasses[size]
              )}
            />
            {message && (
              <p className="text-sm text-gray-600 font-medium animate-pulse">
                {message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
